import React, { useEffect } from "react";
import { Box, Grid, CircularProgress, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  MdLocalShipping,
  MdDoneAll,
  MdPendingActions,
  MdCancel,
  MdGavel,
} from "react-icons/md";
import { AiOutlineClockCircle } from "react-icons/ai";
import { BsCheckCircle, BsXCircle } from "react-icons/bs";

import { fetchBuyerDashboard } from "../../../features/buyer/buyerSlice";
import { timeAgo } from "../../../utils/timeAgo";
import { getStatusColor, capitalizeFirst } from "../../../utils/stringUtils";
import { formatCurrency, formatPrice } from "../../../utils/price";
import theme from "../../../styles/theme/theme";

// Section Components
import HeaderSection from "./components/HeaderSection";
import StatsSection from "./components/StatsSection";
import NotificationsSection from "./components/NotificationsSection";
import RecommendationsSection from "./components/RecommendationsSection";
import RecentOrdersSection from "./components/RecentOrdersSection";
import RecentBidsSection from "./components/RecentBidsSection";
import ProfileCompletionSection from "./components/ProfileCompletionSection";
import SupportSection from "./components/SupportSection";
import KycBanner from "../../../components/common/KycBanner";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dashboard, dashboardLoading, dashboardError } = useSelector(
    (state) => state.buyer,
  );
  const profile = useSelector((state) => state.buyer.profile);

  useEffect(() => {
    dispatch(fetchBuyerDashboard());
  }, [dispatch]);

  const getProfileCompletion = (profile) => {
    if (!profile) return 0;
    let completed = 0;
    const total = 7;
    if (profile.name) completed++;
    if (profile.email) completed++;
    if (profile.phone) completed++;
    if (profile.company_name) completed++;
    if (profile.role) completed++;
    if (profile.trade_preferences) completed++;
    if (profile.kycStatus === "verified") completed++;
    return Math.round((completed / total) * 100);
  };

  const profileCompletion = getProfileCompletion(profile);

  const orderStats = dashboard?.orderStats || {};
  const bidStats = dashboard?.bidStats || {};
  const recentOrders = dashboard?.recentOrders || [];
  const recentBids = dashboard?.recentBids || [];
  const notifications = dashboard?.notifications || [];
  const recommendations = dashboard?.recommendedProducts || [];
  console.log("orders active:", dashboard);
  const orderStatsCards = [
    {
      key: "totalOrders",
      label: "Total Orders",
      value: orderStats.active || 0,
      color: theme.palette.info.main,
      bg: "#e8f0fe",
      icon: <MdLocalShipping />,
    },
    {
      key: "completedOrders",
      label: "Completed Orders",
      value: orderStats.completed || 0,
      color: theme.palette.success.main,
      bg: "#e9fbe9",
      icon: <MdDoneAll />,
    },
    {
      key: "pendingOrders",
      label: "Pending Orders",
      value: orderStats.pending || 0,
      color: theme.palette.warning.main,
      bg: "#fffbe7",
      icon: <MdPendingActions />,
    },
    {
      key: "cancelledOrders",
      label: "Cancelled Orders",
      value: orderStats.cancelled || 0,
      color: theme.palette.error.main,
      bg: "#ffecec",
      icon: <MdCancel />,
    },
  ];

  const bidStatsCards = [
    {
      key: "totalBids",
      label: "Total Bids",
      value: bidStats.active || 0,
      color: theme.palette.info.main,
      bg: "#e8f0fe",
      icon: <MdGavel />,
    },
    {
      key: "pendingBids",
      label: "Pending Bids",
      value: bidStats.pending || 0,
      color: theme.palette.warning.main,
      bg: "#fffbe7",
      icon: <AiOutlineClockCircle />,
    },
    {
      key: "acceptedBids",
      label: "Accepted Bids",
      value: bidStats.accepted || 0,
      color: theme.palette.success.main,
      bg: "#e9fbe9",
      icon: <BsCheckCircle />,
    },
    {
      key: "rejectedBids",
      label: "Rejected Bids",
      value: bidStats.rejected || 0,
      color: theme.palette.error.main,
      bg: "#ffecec",
      icon: <BsXCircle />,
    },
  ];

  const recentOrdersCards = recentOrders.map((order) => ({
    id: `${order.id}`,
    product: order.product?.name || "N/A", // Ensure product name is accessed safely
    status: order.status || "N/A",
    paymentStatus: order.payment_status || "pending",
    statusColor: getStatusColor(order.status),
    date: timeAgo(order.created_at),
  }));

  const recentBidsCards = recentBids.map((bid) => ({
    id: `${bid.id}`,
    productId: bid.product?.id,
    product: bid.product?.name || "N/A",
    amount: formatCurrency(bid.bid_price, bid.currency),
    status: capitalizeFirst(bid.status) || "N/A",
    statusColor: getStatusColor(bid.status),
    date: timeAgo(bid.created_at),
  }));

  const latestNotification = notifications
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  const notificationsCards = latestNotification
    ? [
      {
        id: 1,
        message: latestNotification.message,
        date: timeAgo(latestNotification.created_at),
      },
    ]
    : [];

  const recommendationsCards = recommendations.map((rec) => ({
    id: rec.id,
    name: rec.name,
    price: rec.price,
  }));

  const handlePayNow = (orderId) => navigate(`/shipping-mode/${orderId}`);

  const dashboardRoutes = {
    totalOrders: "/my-orders",
    completedOrders: "/my-orders?status=completed",
    pendingOrders: "/my-orders?status=pending",
    cancelledOrders: "/my-orders?status=cancelled",
    totalBids: "/my-bids",
    pendingBids: "/my-bids?status=pending",
    acceptedBids: "/my-bids?status=accepted",
    rejectedBids: "/my-bids?status=rejected",
  };

  const handleCardClick = (key) => {
    if (dashboardRoutes[key]) {
      navigate(dashboardRoutes[key]);
    }
  };

  const handleOrderRowClick = (orderId) => {
    navigate(`/order-details/${orderId}`);
  };

  const handleBidRowClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  if (dashboardLoading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (dashboardError)
    return (
      <Box textAlign="center" py={10}>
        <Typography color="error">
          Error loading dashboard: {dashboardError}
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#f9fafb 0%,#f1f5f9 100%)",
        p: { xs: 3, md: 8 },
        fontFamily: theme.typography.fontFamily,
        transition: "background 0.3s ease",
      }}
    >
      <HeaderSection title="Dashboard" />

      <KycBanner />

      <StatsSection
        title="Order Statistics"
        stats={orderStatsCards}
        onCardClick={handleCardClick}
      />
      <StatsSection
        title="Bid Statistics"
        stats={bidStatsCards}
        onCardClick={handleCardClick}
      />

      <Grid
        container
        spacing={4}
        mb={8}
        sx={{
          alignItems: "stretch",
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Grid item xs={12} md={6} sx={{ flex: 1 }}>
          <Box sx={{ height: "100%", width: "100%" }}>
            <NotificationsSection
              notifications={notificationsCards}
              theme={theme}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ flex: 1 }}>
          <Box sx={{ height: "100%", width: "100%" }}>
            <RecommendationsSection recommendations={recommendationsCards} />
          </Box>
        </Grid>
      </Grid>

      <RecentOrdersSection
        orders={recentOrdersCards}
        onPay={handlePayNow}
        theme={theme}
        onRowClick={handleOrderRowClick}
      />
      <RecentBidsSection
        bids={recentBidsCards}
        theme={theme}
        onRowClick={handleBidRowClick}
      />

      <Grid
        container
        spacing={2}
        mb={10}
        sx={{
          alignItems: "stretch",
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Grid item xs={12} md={6} sx={{ display: "flex", flex: 1 }}>
          <ProfileCompletionSection
            completion={profileCompletion}
            onNavigateProfile={() => navigate("/profile")}
            theme={theme}
            sx={{ flex: 1 }}
          />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex", flex: 1 }}>
          <SupportSection
            onNavigateContact={() => navigate("/contact-us")}
            theme={theme}
            sx={{ flex: 1 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
